import React from 'react'
import { useTranslation } from 'react-i18next'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import ContrastIcon from '@mui/icons-material/Contrast'
import LoginIcon from '@mui/icons-material/Login'
import { useAuth0 } from '@auth0/auth0-react'
import { isDevelopment, shouldBypassAuth, useMockAuth0 } from '../../devAuth'
import { useTheme } from '../../contexts/ThemeContext'
import logo from '../../assets/logo.svg'
import logoWhite from '../../assets/veta-logo-white.svg'
import styles from './Home.module.scss'

const THEME_ICONS = { auto: ContrastIcon, light: LightModeIcon, dark: DarkModeIcon }
const THEME_LABELS = { auto: 'Auto', light: 'Light', dark: 'Dark' }

const HomePageView = () => {
  const { t } = useTranslation()
  const { theme, resolvedTheme, cycleTheme } = useTheme()
  const mockAuth = useMockAuth0()
  const auth0 = useAuth0()
  const { loginWithRedirect } = mockAuth || auth0

  const enableDevBypass = () => {
    localStorage.setItem('dev_bypass', 'true')
    window.location.reload()
  }

  const ThemeIcon = THEME_ICONS[theme]

  return (
    <div className={styles.homeContainer}>
      {/* Top Navigation Bar */}
      <header className={styles.topBar}>
        <div className={styles.topBarInner}>
          <img src={resolvedTheme === 'dark' ? logoWhite : logo} alt='Veta Virtual' className={styles.topBarLogo} />
          <div className={styles.topBarActions}>
            <button
              className={styles.themeToggle}
              onClick={cycleTheme}
              title={`Theme: ${THEME_LABELS[theme]}`}
              aria-label={`Switch theme, current: ${THEME_LABELS[theme]}`}
            >
              <ThemeIcon className={styles.themeIcon} />
              <span className={styles.themeLabel}>{THEME_LABELS[theme]}</span>
            </button>
            <button
              className={styles.loginButton}
              onClick={() => loginWithRedirect()}
              type='button'
              aria-label='Log in'
            >
              <LoginIcon className={styles.loginIcon} />
              <span>Log In</span>
            </button>
          </div>
        </div>
      </header>

      <div className={styles.contentWrapper}>
        {/* Welcome Section */}
        <div className={styles.welcomeSection}>
          <h1 className={styles.welcomeTitle}>{t('dashboard.welcomeMessage')}</h1>
          <p className={styles.welcomeText}>{t('home.welcome')}</p>
        </div>

        {/* Dev Bypass (Development Only - shown only when logged out) */}
        {isDevelopment && !shouldBypassAuth() && (
          <div className={styles.devBypassSection}>
            <button
              className={styles.devBypassLink}
              onClick={enableDevBypass}
              type='button'
            >
              Dev Bypass Login
            </button>
          </div>
        )}

        {/* Main Content Grid — 3 columns */}
        <div className={styles.contentGrid}>
          {/* Row 1: Phone, Hours, Billing — three quick-reference cards */}
          <div className={styles.infoSection}>
            <h3 className={styles.sectionTitle}>{t('home.mainPhoneNumbers')}</h3>
            <div className={styles.phoneCard}>
              <span className={styles.phoneNumber}><span>CA:</span> 647-250-0403</span>
              <span className={styles.phoneNumber}><span>US:</span> 929-226-0679</span>
            </div>
          </div>

          <div className={styles.infoSection}>
            <h3 className={styles.sectionTitle}>{t('home.hoursOfOperation')}</h3>
            <p className={styles.hoursText}>Monday - Friday</p>
            <p className={styles.hoursText}>9:00 AM - 9:00 PM EST</p>
          </div>

          <div className={styles.infoSection}>
            <h3 className={styles.sectionTitle}>{t('home.billingSchedule')}</h3>
            <p className={styles.policyText}>
              Billing is processed monthly. Prepaid plans include the future month and reconcile additional minutes from the previous month.
            </p>
          </div>

          {/* Row 2: Dashboard Info — full width */}
          <div className={`${styles.infoSection} ${styles.fullWidthSection}`}>
            <h3 className={styles.sectionTitle}>{t('home.dashboardInfo')}</h3>
            <ul className={styles.infoList}>
              <li>{t('home.authentication')}</li>
              <li>{t('home.usageTime')}</li>
              <li>{t('home.usageTotals')}</li>
            </ul>
          </div>

          {/* Row 3: Email Addresses — full width */}
          <div className={`${styles.infoSection} ${styles.fullWidthSection}`}>
            <h3 className={styles.sectionTitle}>{t('home.emailAddresses')}</h3>
            <div className={styles.emailGrid}>
              <div className={styles.contactCard}>
                <a href='mailto:support@vetavirtual.com'>support@vetavirtual.com</a>
                <p>General questions, inquiries, or requests. Monitored by multiple account managers.</p>
              </div>

              <div className={styles.contactCard}>
                <a href='mailto:callnotes@vetavirtual.com'>callnotes@vetavirtual.com</a>
                <p>Receive Call Notes after each call. Reply with concerns about specific calls.</p>
              </div>

              <div className={styles.contactCard}>
                <a href='mailto:outbound@vetavirtual.com'>outbound@vetavirtual.com</a>
                <p>Submit Outbound Call Requests. Get completion confirmations and notes.</p>
              </div>

              <div className={styles.contactCard}>
                <a href='mailto:billing@vetavirtual.com'>billing@vetavirtual.com</a>
                <p>Billing-related requests or inquiries.</p>
              </div>
            </div>
            <div className={styles.emailNote}>
              Add these email addresses and anything ending with <strong>@five9.com</strong> to your contacts to prevent emails going to spam.
            </div>
          </div>

          {/* Row 4: Holiday Schedule — spans 2 columns */}
          <div className={`${styles.infoSection} ${styles.twoColSection}`}>
            <h3 className={styles.sectionTitle}>{t('home.holidaySchedule')}</h3>
            <p className={styles.policyText} style={{ marginBottom: '12px' }}>
              Closed on all US/Canada federal holidays. Missed calls returned next business day.
            </p>
            <table className={styles.holidayTable}>
              <thead>
                <tr>
                  <th>Canada</th>
                  <th>USA</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>New Year&apos;s Day</td><td>New Year&apos;s Day</td></tr>
                <tr><td>Family Day</td><td>MLK Jr. Day</td></tr>
                <tr><td>Good Friday</td><td>Presidents&apos; Day</td></tr>
                <tr><td>Victoria Day</td><td>Memorial Day</td></tr>
                <tr><td>Canada Day</td><td>Independence Day</td></tr>
                <tr><td>Civic Holiday</td><td>Labor Day</td></tr>
                <tr><td>Labor Day</td><td>Columbus Day</td></tr>
                <tr><td>Thanksgiving</td><td>Thanksgiving</td></tr>
                <tr><td>Christmas Day</td><td>Christmas Day</td></tr>
                <tr><td>Boxing Day</td><td></td></tr>
              </tbody>
            </table>
          </div>

          <div className={`${styles.infoSection} ${styles.policySection}`}>
            <h3 className={styles.sectionTitle}>{t('home.cancellationPolicy')}</h3>
            <ul className={styles.infoList}>
              <li>Month-to-month plans require one billing cycle advance notice for cancellation.</li>
              <li>Fixed term commitments require one billing cycle advance notice and may have other requirements.</li>
              <li>
                See the{' '}
                <a
                  href='https://www.vetavirtual.com/terms-of-use/'
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='General Terms and Conditions (opens in new tab)'
                >
                  General Terms and Conditions (opens in new tab)
                </a>
                {' '}for details.
              </li>
            </ul>
          </div>
        </div>

        {/* Version */}
        <p className={styles.versionText}>{t('home.version')}</p>
      </div>
    </div>
  )
}

export default HomePageView
